import { Bar, Doughnut } from "react-chartjs-2";
import {
  createDateRange,
  createPhases,
  REBA_THRESHOLDS_ADJUSTED, saveLayoutData,
  VIEW_MODE_DETAILS
} from "../../../services/ergonomics/ergonomics-service";
import { useErgonomicsContext } from "../../../contexts/ErgonomicsContext";
import { useEffect, useRef } from "react";

const donutOptions = {
  cutout: '80%',
  plugins: {
    legend: false,
  },
};

export const ErgonomicsGraph = ({ stepNumber, maxValue, title, mode="general" }) => {

  const { zoomDateRange, setZoomDateRange, selectedDateRange, data, unzoomedData, maxData, expositionData,
    side, isLiveMode, viewMode, flags, filters, setHoveredTimestamp, hasData, layoutData, setLayoutData } = useErgonomicsContext();

  const range = mode === 'general' ? selectedDateRange : zoomDateRange;
  const dataToUse = mode === 'general' ? unzoomedData : data;

  const hoverResetTimeoutRef = useRef(null);
  useEffect(() => {
    return () => {
      if (hoverResetTimeoutRef.current !== null) {
        clearTimeout(hoverResetTimeoutRef.current);
      }
    };
  }, []);

  /* More info about charts used in this dashboard:
   * https://www.chartjs.org/docs/latest/
   */
  const getOptions = (max=10, title='REBA Score', dateRange=0, onZoom=null, phases=[], maxValue=null) => {
    const hasZoom = zoomDateRange !== null;

    return {
      maintainAspectRatio: false,
      onHover: (event, elements, chart) => {
        if (!elements?.length) {
          if (hoverResetTimeoutRef.current === null) {
            hoverResetTimeoutRef.current = setTimeout(() => {
              setHoveredTimestamp(null);
              hoverResetTimeoutRef.current = null;
            }, 500);
          }
          return;
        }

        if (hoverResetTimeoutRef.current !== null) {
          clearTimeout(hoverResetTimeoutRef.current);
          hoverResetTimeoutRef.current = null;
        }

        const { index, datasetIndex } = elements[0];
        const ds = chart.data.datasets?.[datasetIndex]?.data || [];
        if (!ds.length) return;

        const point = ds[index];
        if (point?.x) {
          setHoveredTimestamp(point.x);
        }
      },
      onClick: (evt, elements, chart) => {
        if (!elements?.length) {
          setZoomDateRange(null);
          const newLayoutData = { ...layoutData, zoomDateRange: null };
          setLayoutData(newLayoutData);
          saveLayoutData(newLayoutData);
          return;
        }

        if (!onZoom) return;

        const { index, datasetIndex } = elements[0];
        const ds = chart.data.datasets?.[datasetIndex]?.data || [];
        if (!ds.length) return;

        // x au format { x: timestamp(ms), y: ... }
        const xCurr = ds[index]?.x;
        if (!Number.isFinite(xCurr)) return;

        const start = xCurr - selectedDateRange.window_s * 1000;
        const end = xCurr;

        onZoom(start, end);
      },
      scales: {
        x: {
          type: 'time',
          position: 'bottom',
          offset: false,
          grid: {
            display: true,
          },
          time: {
            unit: dateRange.unit,
            displayFormats: { hour: 'MMM D, YYYY HH:mm', minute: 'HH:mm' },
          },
          ticks: {
            stepSize: dateRange.stepSize,
          },
          min: dateRange.start,
          max: dateRange.stop,
          title: {
            display: false,
            text: 'Time',
          },
        },
        y: {
          min: 0,
          // max: max,
          beginAtZero: true,
          grid: {
            display: true,
          },
          title: {
            display: true,
            text: title,
          },
          ticks: {
            stepSize: 2,
          },
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        zoom: {
          pan: false,
          zoom: {
            mode: 'x',
            drag: { enabled: true, backgroundColor: 'rgba(65, 100, 253,0.5)' },
            onZoomComplete: ({ chart }) => {
              const start = chart.scales.x.min;
              const end = chart.scales.x.max;
              // console.log(onZoom, start, end);
              if (onZoom !== null)
                onZoom(start, end);
            },
          },
        },
        annotation: {
          annotations: {
            // ...phases,
            ...(hasZoom && mode === 'general'
              ? {
                zoomOverlay: {
                  type: 'box',
                  xMin: zoomDateRange.start,
                  xMax: zoomDateRange.stop,
                  backgroundColor: 'rgba(65, 100, 253, 0.3)',
                  borderColor: 'rgba(65, 100, 253, 0.5)',
                  borderWidth: 1,
                },
              }
              : {}),
            ...(maxValue && mode !== 'general'
              ? {
                lineMax: {
                  type: 'line',
                  yMin: maxValue,
                  yMax: maxValue,
                  borderColor: 'red',
                  borderWidth: 1,
                  label: {
                    enabled: false,
                  },
                },
              }
              : {}),
          },
        },
      },
    };
  };

  const getBarColor = (context, key) => {
    const value = context.raw?.y ?? 0;

    if (REBA_THRESHOLDS_ADJUSTED[key].length === 3) {
      if (value >= REBA_THRESHOLDS_ADJUSTED[key][2]) return 'rgba(255, 99, 132, 0.8)'; // Red
      else if (value >= REBA_THRESHOLDS_ADJUSTED[key][1]) return 'rgba(255, 206, 86, 0.8)'; // Yellow
      else return 'rgba(75, 255, 192, 0.8)'; // Green
    }
    else {
      if (value >= REBA_THRESHOLDS_ADJUSTED[key][4]) return 'rgba(255, 120, 150, 0.9)'; // Bright red
      else if (value >= REBA_THRESHOLDS_ADJUSTED[key][3]) return 'rgba(255, 99, 132, 0.8)'; // Red
      else if (value >= REBA_THRESHOLDS_ADJUSTED[key][2]) return 'rgba(255, 150, 86, 0.8)'; // Orange
      else if (value >= REBA_THRESHOLDS_ADJUSTED[key][1]) return 'rgba(255, 206, 86, 0.8)'; // Yellow
      else return 'rgba(75, 255, 192, 0.8)'; // Green
    }
  };

  const getExpositionColor = (context) => {
    const value = context.raw ?? 0;

    if (value >= 70) return 'rgba(255, 99, 132, 0.8)'; // Red
    else if (value >= 30) return 'rgba(255, 206, 86, 0.8)' // Yellow
    else return 'rgba(75, 255, 192, 0.8)'; // Green
  };

  const filterPointsByFlags = (points) => {
    if(points === null || points === undefined) return [];

    return points.map((point, index) => {
      const timestamp = new Date(point.time).getTime();

      if ((index < flags.length && flags[index]) || mode !== 'general') {
        return { x: timestamp, y: point.value};
      } else {
        return { x: timestamp, y: 0 };
      }
    });
  };

  const getExpositionDuration = (key) => {
    return expositionData['step_' + key] || 0;
  };

  const handleZoom = (start, end) => {
    setZoomDateRange(createDateRange(Math.round(start), Math.round(end)));

    // Save layout data
    const newLayoutData = { ...layoutData, zoomDateRange: { start, end } };
    setLayoutData(newLayoutData);
    saveLayoutData(newLayoutData);
  };

  const filtersDisplayText = Object.fromEntries(filters.data.map(o => [o.name, o.display_text]));
  const phases = createPhases(data, filtersDisplayText);

  return (
    <div className='h-full flex flex-col gap-4'>
      <div className='flex h-full'>
        <div className='w-full'>
          <Bar data={{label: 'Score ' + title, datasets: [{
              data: hasData ? filterPointsByFlags(dataToUse['step_' + stepNumber]) : [],
              borderColor: (context) => getBarColor(context, 'step_' + stepNumber),
              backgroundColor: (context) => getBarColor(context, 'step_' + stepNumber),
              pointStyle: false,
              tension: 0.3,
              fill: true,
            }]}} options={getOptions(maxValue, title, range, handleZoom, phases, maxData['step_' + stepNumber + '_' + side])}/>
        </div>
        {
          mode !== 'general' &&
          <>
            {
              !isLiveMode && viewMode === VIEW_MODE_DETAILS ?
                <div className='flex flex-col items-center justify-center w-1/6'>
                  <p className='mb-2 text-xs'>Taux d'exposition</p>
                  <div className='size-16 relative'>
                    <Doughnut data={{
                      datasets: [
                        {
                          data: [getExpositionDuration(stepNumber), 100 - getExpositionDuration(stepNumber)],
                          backgroundColor: (context) => [getExpositionColor(context), 'rgba(67, 67, 67, 1)'],
                          borderWidth: 0,
                        },
                      ],
                    }} options={donutOptions} />
                    <div className='absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2'>{getExpositionDuration(stepNumber)}%</div>
                  </div>
                </div>
                :
                <div className='flex flex-col items-center justify-center w-1/6 text-xs text-center'>
                  Taux d'exposition indisponible
                </div>
            }
          </>
        }
      </div>
    </div>
  );
};